import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const GA_MEASUREMENT_ID = "G-VZFH5TMNWM";

const VercelAnalytics = lazy(() =>
  import("@vercel/analytics/react").then((module) => ({
    default: module.Analytics,
  }))
);

let lastTrackedPage = "";

function scheduleDeferredStart(callback) {
  if (typeof window === "undefined") {
    return () => {};
  }

  let started = false;
  let idleId = null;
  const timeoutId = window.setTimeout(start, 5000);
  const events = ["pointerdown", "keydown", "touchstart", "scroll"];

  function start() {
    if (started) return;
    started = true;
    cleanup();
    callback();
  }

  function cleanup() {
    window.clearTimeout(timeoutId);
    if (idleId !== null && "cancelIdleCallback" in window) {
      window.cancelIdleCallback(idleId);
    }
    events.forEach((eventName) => {
      window.removeEventListener(eventName, start);
    });
  }

  events.forEach((eventName) => {
    window.addEventListener(eventName, start, { passive: true, once: true });
  });

  if ("requestIdleCallback" in window) {
    idleId = window.requestIdleCallback(start, { timeout: 2500 });
  } else {
    window.setTimeout(start, 2500);
  }

  return cleanup;
}

export default function DeferredAnalytics() {
  const location = useLocation();
  const pagePath = getPagePath(location);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => scheduleDeferredStart(() => setShouldLoad(true)), []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const timeoutId = window.setTimeout(() => {
      trackGooglePageView(pagePath);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [pagePath]);

  if (!shouldLoad) return null;

  return (
    <Suspense fallback={null}>
      <VercelAnalytics />
    </Suspense>
  );
}

function getPagePath(location) {
  return `${location.pathname}${location.search}${location.hash}`;
}

function trackGooglePageView(pagePath) {
  if (typeof window.gtag !== "function") return;

  if (pagePath === lastTrackedPage) return;

  lastTrackedPage = pagePath;
  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: window.location.href,
    page_path: pagePath,
    send_to: GA_MEASUREMENT_ID,
  });
}
